import { CreditCard, Layers, Link2, X, Minus, Plus as PlusIcon, Clock, ExternalLink, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import DynamicCardFields from "@/components/activities/DynamicCardFields";
import TransitionEditor, { type ActivityCardInfo, type ActivityGroupInfo } from "@/components/activities/TransitionEditor";
import type {
  EditorStep,
  EditorCardStep,
  EditorCardGroupStep,
  EditorTransition,
  TemplateContentFields,
} from "@/types/activity";
import { Trash2 } from "lucide-react";

/* ─── Duration stepper ────────────────────────────────────── */

function DurationStepper({
  value,
  onChange,
}: {
  value?: number;
  onChange: (v: number | undefined) => void;
}) {
  const current = value ?? 1;
  return (
    <div className="ml-auto flex items-center gap-1 text-xs text-muted-foreground shrink-0">
      <Clock className="h-3 w-3" />
      <span className="whitespace-nowrap">Durée :</span>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-5 w-5"
        onClick={() => onChange(Math.max(0, current - 1))}
      >
        <Minus className="h-3 w-3" />
      </Button>
      <span className="w-4 text-center font-medium text-foreground">{current}</span>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-5 w-5"
        onClick={() => onChange(current + 1)}
      >
        <PlusIcon className="h-3 w-3" />
      </Button>
      <span>min</span>
    </div>
  );
}

/* ─── helpers ─────────────────────────────────────────────── */

function StepDisplayLabel({ step }: { step: EditorStep }) {
  if (step.type === "card") {
    return <>{step.templateLabel || "Carte"}</>;
  }
  return <>{(step as EditorCardGroupStep).title || "Groupe"}</>;
}

/* ─── Types for edge selection ─────────────────────────────── */

export interface SelectedEdge {
  sourceStepLocalId: string;
  transitionIndex: number;
}

/* ─── Props ────────────────────────────────────────────────── */

interface StepConfigPanelProps {
  // Node selection
  selectedStep: EditorStep | null;
  allSteps: EditorStep[];
  // Edge selection
  selectedEdge: SelectedEdge | null;
  // Templates
  templates: { id: string; code: string; label: string; is_interactive?: boolean }[];
  getTemplateFields: (templateId: string) => TemplateContentFields | null;
  // Cards info for response_value conditions
  activityCards: ActivityCardInfo[];
  // Groups info for score conditions
  activityGroups?: ActivityGroupInfo[];
  // Validation errors for the selected step
  validationErrors?: import("@/lib/validateCardContent").ValidationError[];
  // Whether activity duration is forced (hides card-level duration stepper)
  isDurationForced?: boolean;
  // Parent path type (for meteo score type selector on groups)
  pathType?: string | null;
  // Read-only mode (published activity)
  readOnly?: boolean;
  // Callbacks
  onUpdateStep: (localId: string, updates: Partial<EditorStep>) => void;
  onAddCardToGroup: (stepLocalId: string, templateId: string) => void;
  onRemoveCardFromGroup: (stepLocalId: string, cardIdx: number) => void;
  onUpdateGroupCard: (stepLocalId: string, cardIdx: number, updates: Partial<EditorCardStep>) => void;
  onUpdateTransitions: (stepLocalId: string, transitions: EditorTransition[]) => void;
  onClose: () => void;
  onOpenGroupEditor?: (stepLocalId: string) => void;
}

export default function StepConfigPanel({
  selectedStep,
  allSteps,
  selectedEdge,
  templates,
  getTemplateFields,
  activityCards,
  activityGroups,
  validationErrors,
  isDurationForced,
  pathType,
  readOnly,
  onUpdateStep,
  onAddCardToGroup,
  onRemoveCardFromGroup,
  onUpdateGroupCard,
  onUpdateTransitions,
  onClose,
  onOpenGroupEditor,
}: StepConfigPanelProps) {
  // If an edge is selected, show the transition config for that edge's source step
  if (selectedEdge) {
    const sourceStep = allSteps.find((s) => s.localId === selectedEdge.sourceStepLocalId);
    if (!sourceStep) return null;

    return (
      <div className="h-full flex flex-col">
        <ScrollArea className="flex-1 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Link2 className="h-4 w-4 text-primary" />
            <span className="font-medium text-sm">Transition</span>
          </div>
          <TransitionEditor
            step={sourceStep}
            allSteps={allSteps}
            activityCards={activityCards}
            activityGroups={activityGroups}
            onChange={(transitions) => onUpdateTransitions(sourceStep.localId, transitions)}
            singleTransitionIndex={selectedEdge.transitionIndex}
            readOnly={readOnly}
          />
        </ScrollArea>
      </div>
    );
  }

  if (!selectedStep) return null;

  const errorFieldNames = new Set(validationErrors?.map((e) => e.fieldName) ?? []);

  return (
    <div className="h-full flex flex-col">
      {/* Element header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
        {selectedStep.type === "card" ? (
          <CreditCard className="h-4 w-4 text-primary" />
        ) : (
          <Layers className="h-4 w-4 text-primary" />
        )}
        <span className="font-medium text-sm">
          <StepDisplayLabel step={selectedStep} />
        </span>
        <Badge variant="outline" className={`text-xs ${selectedStep.type === "card_group" ? "border-group-pale text-group" : ""}`}>
          {selectedStep.type === "card" ? "Carte" : "Groupe"}
        </Badge>
        {selectedStep.type === "card" && !isDurationForced && !readOnly && (
          <DurationStepper
            value={selectedStep.estimatedDurationMinutes}
            onChange={(v) => onUpdateStep(selectedStep.localId, { estimatedDurationMinutes: v } as any)}
          />
        )}
      </div>

      {/* Body */}
      <ScrollArea className="flex-1 p-4">
        {selectedStep.type === "card" ? (
          <CardStepConfig
            step={selectedStep}
            fields={getTemplateFields(selectedStep.templateId)}
            onChange={(updates) => onUpdateStep(selectedStep.localId, updates)}
            errorFieldNames={errorFieldNames}
            readOnly={readOnly}
          />
        ) : (
          <CardGroupStepConfig
            step={selectedStep as EditorCardGroupStep}
            onChange={(updates) => onUpdateStep(selectedStep.localId, updates as any)}
            onOpenGroupEditor={() => onOpenGroupEditor?.(selectedStep.localId)}
            pathType={pathType}
            readOnly={readOnly}
          />
        )}

        {/* Transitions section */}
        <div className="mt-6 pt-4 border-t border-border">
          <TransitionEditor
            step={selectedStep}
            allSteps={allSteps}
            activityCards={activityCards}
            activityGroups={activityGroups}
            onChange={(transitions) => onUpdateTransitions(selectedStep.localId, transitions)}
            readOnly={readOnly}
          />
        </div>
      </ScrollArea>
    </div>
  );
}

/* ─── Lottie inline thumbnail (kept for media picker grid) ── */


/* ─── Card step config ────────────────────────────────────── */

function CardStepConfig({
  step,
  fields,
  onChange,
  errorFieldNames,
  readOnly,
}: {
  step: EditorCardStep;
  fields: TemplateContentFields | null;
  onChange: (updates: Partial<EditorCardStep>) => void;
  errorFieldNames?: Set<string>;
  readOnly?: boolean;
}) {
  

  return (
    <div className="space-y-4">
      {/* Hide title on mobile toggle */}
      <div className="flex items-center justify-between rounded-md border border-border p-3">
        <Label className="text-sm">Masquer le titre sur mobile</Label>
        <Switch
          checked={!!step.content?.hide_title_mobile}
          onCheckedChange={(checked) => onChange({ content: { ...step.content, hide_title_mobile: checked } })}
          disabled={readOnly}
        />
      </div>
      {fields?.fields && (
        <DynamicCardFields
          fields={fields.fields}
          values={step.content}
          onChange={(content) => onChange({ content })}
          templateCode={step.templateCode}
          errorFieldNames={errorFieldNames}
          disabled={readOnly}
        />
      )}
      {!readOnly && (
        <div className="space-y-3">
          {/* Tooltips section */}
          {step.tooltips ? (
            <div className="space-y-2 rounded-md border border-border p-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Infobulle – Sources</Label>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-destructive"
                  onClick={() => onChange({ tooltips: null })}
                >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            <Input
              value={step.tooltips?.title || ""}
              onChange={(e) =>
                onChange({ tooltips: { ...step.tooltips, title: e.target.value || undefined, items: step.tooltips?.items || [] } })
              }
              placeholder="Titre (ex: Sources scientifiques)"
            />
            {(step.tooltips?.items || []).map((item, idx) => (
              <div key={idx} className="flex gap-2 items-start">
                <div className="flex-1 space-y-1">
                  <Input
                    value={item.description}
                    onChange={(e) => {
                      const newItems = [...(step.tooltips?.items || [])];
                      newItems[idx] = { ...newItems[idx], description: e.target.value };
                      onChange({ tooltips: { ...step.tooltips, items: newItems } });
                    }}
                    placeholder="Description"
                  />
                  <Input
                    value={item.url}
                    onChange={(e) => {
                      const newItems = [...(step.tooltips?.items || [])];
                      newItems[idx] = { ...newItems[idx], url: e.target.value };
                      onChange({ tooltips: { ...step.tooltips, items: newItems } });
                    }}
                    placeholder="https://…"
                  />
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="shrink-0 mt-0.5"
                  onClick={() => {
                    const newItems = (step.tooltips?.items || []).filter((_, i) => i !== idx);
                    onChange({ tooltips: { ...step.tooltips, items: newItems } });
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const newItems = [...(step.tooltips?.items || []), { url: "", description: "" }];
                onChange({ tooltips: { ...step.tooltips, items: newItems } });
              }}
            >
              + Ajouter un lien
            </Button>
          </div>
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() => onChange({ tooltips: { title: "Sources scientifiques", items: [] } })}
          >
            <Link2 className="h-4 w-4 mr-1" />
            Préciser des sources en infobulle
          </Button>
        )}
        </div>
      )}
    </div>
  );
}

/* ─── Card group step config ──────────────────────────────── */

function CardGroupStepConfig({
  step,
  onChange,
  onOpenGroupEditor,
  pathType,
  readOnly,
}: {
  step: EditorCardGroupStep;
  onChange: (updates: Partial<EditorCardGroupStep>) => void;
  onOpenGroupEditor?: () => void;
  pathType?: string | null;
  readOnly?: boolean;
}) {

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        <div className="space-y-1.5">
          <Label className="text-sm">Titre du groupe <span className="text-destructive">*</span></Label>
            <Input
              value={step.title}
              onChange={(e) => onChange({ title: e.target.value })}
              placeholder="Nom du groupe"
              disabled={readOnly}
            />
        </div>

        {(pathType === "meteo" || pathType === "onboarding") && (
          <div className="space-y-1.5">
            <Label className="text-sm">Type de score météo</Label>
            <Select
              value={(step.config as any)?.meteo_score_type || "__none__"}
              disabled={readOnly}
              onValueChange={(v) => {
                const newConfig = { ...(step.config as Record<string, unknown> || {}) };
                if (v === "__none__") {
                  delete newConfig.meteo_score_type;
                } else {
                  newConfig.meteo_score_type = v;
                }
                onChange({ config: Object.keys(newConfig).length > 0 ? newConfig : null } as any);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Non comptabilisé" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Non comptabilisé</SelectItem>
                <SelectItem value="wellbeing">Bien-être</SelectItem>
                <SelectItem value="suffering">Souffrance</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Link to open group editor */}
      <Button
        variant="outline"
        size="sm"
        className="w-full border-group/30 text-group hover:bg-group/10 hover:text-group-foreground gap-2"
        onClick={() => onOpenGroupEditor?.()}
        disabled={readOnly}
      >
        <Layers className="h-4 w-4" />
        {readOnly ? "Voir" : "Gérer"} les cartes du groupe ({step.cards.length})
        <ExternalLink className="h-3 w-3 ml-auto" />
      </Button>
    </div>
  );
}
